import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Hvor digital er restauranten din allerede?",
  },
  {
    id: "definition",
    label: "Hva betyr digitalisering av restaurant?",
  },
  {
    id: "why",
    label: "Hvorfor restaurantdigitalisering er viktig nå",
  },
  {
    id: "steps",
    label: "Hvordan starte restaurantdigitalisering steg for steg",
  },
  {
    id: "channels",
    label: "De viktigste digitale byggesteinene for restauranter",
  },
  {
    id: "guest",
    label: "Tenk på digitalisering fra gjestens perspektiv",
  },
  {
    id: "team",
    label: "Digitalisering må også hjelpe teamet",
  },
  {
    id: "platforms",
    label: "Hvorfor restauranter trenger digital uavhengighet",
  },
  {
    id: "mistakes",
    label: "Vanlige feil ved restaurantdigitalisering",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place kan hjelpe med restaurantdigitalisering",
  },
  {
    id: "faq",
    label: "FAQ om restaurantdigitalisering",
  },
];

const checklist = [
  "Menyen din er vanskelig å finne online eller ikke oppdatert.",
  "Du vil koble QR-kode, meny, reservasjon og nettside bedre sammen.",
  "Reservasjoner kommer gjennom flere kanaler på en ustrukturert måte.",
  "Du oppdaterer regelmessig priser, bilder eller tilgjengelighet.",
  "Du vil være mindre avhengig av papir, PDF-er eller manuelle prosesser.",
  "Gjester forventer digital informasjon før de besøker restauranten.",
  "Du vil forenkle prosesser uten å erstatte personlig service.",
  "Du vil bli mer digital steg for steg, uten å innføre et stort system med en gang.",
  "Du vil være mindre avhengig av bare plattformer og sosiale medier.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Digitalisering haster kanskje ikke ennå",
      text:
        "Hvis nøkkelinformasjonen din allerede er oppdatert online og de daglige prosessene fungerer godt, kan du starte lite: meny, QR-kode eller en enkel nettside.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Digitalisering steg for steg gir mening",
      text:
        "Restauranten din kan ha nytte av å koble enkelte digitale byggesteiner sammen: meny, QR-kode, nettside, reservasjon og menyadministrasjon.",
      tone: "medium",
    };
  }

  return {
    title: "Digitalisering kan bli en sentral løftestang",
    text:
      "Flere punkter viser at restauranten din trenger en tydelig digital strategi: bedre synlighet online, en mer oppdatert meny, mindre manuelt arbeid og enklere veier for gjester.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantDigitalizationNo() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Restaurantdigitalisering: bli digital steg for steg | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/restaurant-digitalisierung",
          en: "/en/restaurant-digitalization",
          no: "/no/restaurant-digitalisering",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurantdigitalisering: hvordan restauranter blir digitale steg
              for steg
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Restaurantdigitalisering betyr ikke å erstatte personlig service.
                Det betyr å bruke riktige digitale byggesteiner der de faktisk
                hjelper gjester og teamet.
              </p>

              <p className="seoHero__text">
                For mange restauranter starter digitalisering ikke med et stort
                system. Det starter med enkle spørsmål: Er menyen vår oppdatert
                online? Kan gjester reservere digitalt? Fungerer QR-koden? Kan
                teamet raskt oppdatere priser, bilder og tilgjengelighet?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvor digital er restauranten din allerede?</h2>

            <p>
              Trykk på utsagnene som passer for restauranten din. Resultatet er
              bevisst ærlig: ikke alle restauranter trenger et stort digitalt
              system med en gang, men mange kan ha nytte av å koble meny,
              QR-kode, nettside og reservasjon sammen steg for steg.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Hva betyr digitalisering av restaurant?</h2>

          <p>
            Restaurantdigitalisering betyr å støtte analoge eller manuelle
            prosesser med digitale verktøy der det gir mening: meny, QR-kode,
            reservasjon, bestilling, betaling, nettside, anmeldelser,
            administrasjon og interne arbeidsflyter.
          </p>

          <p>
            Det handler ikke om å automatisere hver eneste gjesteinteraksjon. God
            gjestfrihet forblir personlig. Digitalisering bør redusere unødvendig
            friksjon: færre utdaterte detaljer, færre tapte forespørsler og
            mindre dobbelt manuelt arbeid.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digital meny.</li>
            <li className="text__seo-li">QR-kode-tilgang.</li>
            <li className="text__seo-li">Restaurantnettside.</li>
            <li className="text__seo-li">Online bordreservasjon.</li>
            <li className="text__seo-li">Online menyadministrasjon.</li>
            <li className="text__seo-li">Digitale bestillingsflyter.</li>
            <li className="text__seo-li">Kontaktløs betaling.</li>
            <li className="text__seo-li">Strukturert administrasjon.</li>
          </ul>

          <h3>Digitale verktøy bør redusere friksjon</h3>

          <p>
            De beste digitale verktøyene gjør ikke restauranten kaldere eller mer
            komplisert. De gjør informasjon enklere å finne, endringer enklere å
            administrere og gjestereisen enklere å forstå.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor restaurantdigitalisering er viktig nå</h2>

            <p>
              Gjester bruker allerede digitale veier i hverdagen: de søker etter
              restauranter online, sjekker menyer, reserverer bord og foretrekker
              i økende grad kontaktløs betaling.
            </p>

            <p>
              En restaurant trenger ikke å digitalisere alt. Men den bør dekke de
              viktigste kontaktpunktene godt: synlighet online, aktuell
              menyinformasjon, QR-kode-tilgang, reservasjon og mobilvennlighet.
            </p>

            <h3>Start der gjester allerede handler digitalt</h3>

            <p>
              Den praktiske konklusjonen er enkel: digitalisering bør begynne der
              gjester allerede bruker digitale verktøy — søk, meny, reservasjon
              og betaling.
            </p>
          </div>
        </section>

        <section id="steps" className="acquaintance">
          <h2>Hvordan starte restaurantdigitalisering steg for steg</h2>

          <p>
            Den vanligste feilen er å se på digitalisering som ett stort
            alt-eller-ingenting-prosjekt. For mange restauranter er en
            steg-for-steg-tilnærming bedre.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              Gjør den viktigste informasjonen tydelig online.
            </li>

            <li className="text__seo-li">Opprett en digital meny.</li>

            <li className="text__seo-li">
              Bruk QR-kode-tilgang for gjester.
            </li>

            <li className="text__seo-li">
              Gjør menyen redigerbar online.
            </li>

            <li className="text__seo-li">
              Legg til reservasjon som neste steg.
            </li>

            <li className="text__seo-li">
              Legg senere til bestilling eller betaling hvis det gir mening.
            </li>

            <li className="text__seo-li">
              Sjekk regelmessig hva gjestene faktisk bruker.
            </li>
          </ol>

          <h3>Digitalisering er et system av nyttige forbedringer</h3>

          <p>
            Det er ikke én knapp og ikke én enorm lansering. Det er en rekke små
            forbedringer som gjør restauranten enklere å finne, enklere å forstå
            og enklere å administrere.
          </p>

          <VisualIdea>
            Et steg-for-steg veikart for digitalisering: nettside, digital meny,
            QR-kode, online reservasjon, menyadministrasjon og senere bestilling
            eller betaling.
          </VisualIdea>
        </section>

        <section id="channels" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>De viktigste digitale byggesteinene for restauranter</h2>

            <p>
              En restaurant trenger ikke all teknologi med en gang. Det viktigste
              er å velge byggesteinene som passer for forretningsmodellen.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Digitalt kjernegrunnlag</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Digital meny:</strong> Vis menyen online og hold
                    priser og bilder oppdatert.
                  </li>

                  <li className="text__seo-li">
                    <strong>QR-kode:</strong> Gi gjester rask tilgang ved bordet
                    eller i vinduet.
                  </li>

                  <li className="text__seo-li">
                    <strong>Nettside:</strong> Samle meny, kontakt,
                    åpningstider og reservasjon.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Neste digitale steg</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Online reservasjon:</strong> Motta
                    reservasjonsforespørsler utenfor åpningstid.
                  </li>

                  <li className="text__seo-li">
                    <strong>Bestillingssystem:</strong> Gjør henting,
                    bordbestilling eller forespørsler mer strukturert.
                  </li>

                  <li className="text__seo-li">
                    <strong>Digital betaling:</strong> Gjør betaling raskere og
                    mer praktisk.
                  </li>
                </ul>
              </div>
            </div>

            <div className="seoCompareSmall">
              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/digital-meny-for-restauranter">
                    Digital meny
                  </Link>
                </p>
              </div>

              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/online-bordreservasjon-for-restauranter">
                    Online bordreservasjon
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="guest" className="acquaintance">
          <h2>Tenk på digitalisering fra gjestens perspektiv</h2>

          <p>
            Gjester tenker ikke i programvaremoduler. De har enkle spørsmål:
            Hvor finner jeg restauranten? Hva kan jeg spise der? Hva koster det?
            Kan jeg reservere? Kan jeg betale? Er informasjonen oppdatert?
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Finn restaurant.</li>
            <li className="text__seo-li">Se meny.</li>
            <li className="text__seo-li">Bruk QR-kode.</li>
            <li className="text__seo-li">Reserver bord.</li>
            <li className="text__seo-li">Planlegg besøk.</li>
          </ol>

          <p>
            Jo kortere og tydeligere denne veien er, desto mindre sannsynlig er
            det at gjesten mister orienteringen underveis.
          </p>
        </section>

        <section id="team" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Digitalisering må også hjelpe teamet</h2>

            <p>
              Et digitalt system er bare godt hvis det ikke bare ser bra ut for
              gjester, men også fungerer i teamets daglige rutine.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Dårlig digitalisering</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">For mange separate verktøy.</li>
                  <li className="text__seo-li">Komplisert bruk.</li>
                  <li className="text__seo-li">Uklare ansvarsområder.</li>
                  <li className="text__seo-li">
                    Mer arbeid i stedet for mindre arbeid.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>God digitalisering</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Tydelige arbeidsflyter.</li>
                  <li className="text__seo-li">Enkel administrasjon.</li>
                  <li className="text__seo-li">Oppdatert informasjon.</li>
                  <li className="text__seo-li">Mindre dobbeltarbeid.</li>
                  <li className="text__seo-li">Mer tid til gjester.</li>
                </ul>
              </div>
            </div>

            <p>
              En digital meny som ingen oppdaterer, er ikke nyttig. En
              reservasjonsfunksjon som teamet ser for sent, skaper nye problemer.
              Digitalisering må passe den ekte arbeidsflyten.
            </p>
          </div>
        </section>

        <section id="platforms" className="acquaintance">
          <h2>Hvorfor restauranter trenger digital uavhengighet</h2>

          <p>
            Plattformer, sosiale medier og søkemotorer er viktige. De kan gi
            rekkevidde. Likevel bør en restaurant ikke være fullstendig avhengig
            av eksterne kanaler.
          </p>

          <p>
            Restaurantens eget digitale grunnlag — nettside, meny, QR-kode,
            reservasjon — gir mer kontroll over innhold, presentasjon og
            gjestereise.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Bare eksterne kanaler</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Mindre kontroll over presentasjon.
                </li>

                <li className="text__seo-li">
                  Informasjon spres på flere plattformer.
                </li>

                <li className="text__seo-li">
                  Layout og regler kan endres.
                </li>

                <li className="text__seo-li">
                  Meny, QR-kode og reservasjon kan forbli frakoblet.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Eget digitalt grunnlag</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Sentral restaurantside.</li>
                <li className="text__seo-li">Egen digital meny.</li>
                <li className="text__seo-li">QR-kode-destinasjon.</li>
                <li className="text__seo-li">Reservasjonsvei.</li>
                <li className="text__seo-li">
                  Mer kontroll over gjestereisen.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Vanlige feil ved restaurantdigitalisering</h2>

            <p>
              Digitalisering bør gjøre daglig arbeid tydeligere, ikke tyngre.
              Mange problemer oppstår når en restaurant starter med for mange
              verktøy eller uten et tydelig mål.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    For mange verktøy innføres samtidig.
                  </li>

                  <li className="text__seo-li">
                    Den digitale endringen har ikke et tydelig mål.
                  </li>

                  <li className="text__seo-li">
                    Gjestereisen testes ikke på smarttelefon.
                  </li>

                  <li className="text__seo-li">
                    Teamet involveres ikke i arbeidsflyten.
                  </li>

                  <li className="text__seo-li">
                    Gamle priser, åpningstider eller bilder blir liggende
                    online.
                  </li>

                  <li className="text__seo-li">
                    Restauranten er bare avhengig av eksterne plattformer.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Start med ett nyttig digitalt steg.
                  </li>

                  <li className="text__seo-li">
                    Definer hvilket problem verktøyet løser.
                  </li>

                  <li className="text__seo-li">
                    Test flyten på ekte smarttelefoner.
                  </li>

                  <li className="text__seo-li">Involver teamet tidlig.</li>

                  <li className="text__seo-li">
                    Hold meny og restaurantinformasjon oppdatert.
                  </li>

                  <li className="text__seo-li">
                    Bygg ditt eget digitale grunnlag.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om restaurantdigitalisering</h2>

          <div className="seoFaq">
            <details>
              <summary>
                Må en restaurant digitalisere alt med en gang?
              </summary>

              <p>
                Nei. En steg-for-steg-tilnærming er ofte bedre: digital meny,
                QR-kode, nettside, reservasjon og deretter flere funksjoner.
              </p>
            </details>

            <details>
              <summary>Erstatter digitalisering personlig service?</summary>

              <p>
                Nei. God digitalisering støtter service ved å gjøre informasjon,
                reservasjoner og menyadministrasjon enklere.
              </p>
            </details>

            <details>
              <summary>Hva bør digitaliseres først?</summary>

              <p>
                Menyen er ofte det beste startpunktet, fordi gjester vil se den
                før de besøker restauranten og før de bestiller.
              </p>
            </details>

            <details>
              <summary>Er digitalisering nyttig for små restauranter?</summary>

              <p>
                Ja, spesielt når små bedrifter vil administrere endringer selv
                og være mindre avhengige av trykk, PDF-er eller ekstern hjelp.
              </p>
            </details>

            <details>
              <summary>Hvilken rolle spiller QR-kode?</summary>

              <p>
                QR-kode kan være det raske inngangspunktet: gjester skanner
                koden og åpner menyen, restaurantsiden eller reservasjonsflyten.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
          lang="no"
          group="business"
          exclude="/no/restaurant-digitalisering"
        />
      </article>
    </main>
  );
}